/**
 * 游戏账号状态枚举
 */
export enum AccountStatusEnum {
    DRAFT = 'draft',           // 草稿
    PENDING = 'pending',       // 待审核
    AVAILABLE = 'available',   // 可租赁
    LOCKED = 'locked',         // 锁定中
    RENTED = 'rented',         // 租赁中
    PENDING_CONFIRM = 'pending_confirm', // 待号主确认
    AWAIT_FINAL = 'await_final', // 待最终确认
    REJECTED = 'rejected',     // 审核拒绝
    BANNED = 'banned',         // 已封禁
    DELETED = 'deleted'        // 已删除
}

/**
 * 账号状态显示文本映射
 */
export const ACCOUNT_STATUS_LABELS: Record<AccountStatusEnum, string> = {
    [AccountStatusEnum.DRAFT]: '草稿',
    [AccountStatusEnum.PENDING]: '待审核',
    [AccountStatusEnum.AVAILABLE]: '可租赁',
    [AccountStatusEnum.LOCKED]: '锁定中',
    [AccountStatusEnum.RENTED]: '租赁中',
    [AccountStatusEnum.PENDING_CONFIRM]: '待号主确认',
    [AccountStatusEnum.AWAIT_FINAL]: '待最终确认',
    [AccountStatusEnum.REJECTED]: '审核拒绝',
    [AccountStatusEnum.BANNED]: '已封禁',
    [AccountStatusEnum.DELETED]: '已删除'
}

/**
 * 保险格数枚举
 */
export enum InsuranceSlotsEnum {
    SLOTS_2 = 2,    // 2格保险
    SLOTS_4 = 4,    // 4格保险
    SLOTS_6 = 6,    // 6格保险
    SLOTS_9 = 9     // 9格保险
}

/**
 * 登录平台枚举
 */
export enum LoginMethodEnum {
    QQ = 'qq',          // QQ登录
    WECHAT = 'wechat',  // 微信登录
    STEAM = 'steam'     // Steam 登录
}

/**
 * 登录方式枚举
 */
export enum LoginSubMethodEnum {
    PASSWORD = 'password',     // 账密登录
    QRCODE = 'qrcode'         // 扫码登录
}

/**
 * 登录方式显示文本映射
 */
export const LOGIN_METHOD_LABELS: Record<LoginMethodEnum, string> = {
    [LoginMethodEnum.QQ]: 'QQ登录',
    [LoginMethodEnum.WECHAT]: '微信登录',
    [LoginMethodEnum.STEAM]: 'Steam 登录'
}

// 导入关联类型
import type { GameSkin } from './skins'
import type { GunSkin } from './gunSkins'
import type { OperatorSkin } from './operatorSkins'
import type { GameRank } from './ranks'
import type { User, UserDetail } from './user'
import type { PaginationData } from '../request'

/**
 * 账号收藏状态标签枚举
 */
export enum AccountFavoriteStatusLabel {
    DELETED = 'deleted',      // 账号已删除
    UNAVAILABLE = 'unavailable', // 账号已下架
    AVAILABLE = 'available'      // 正常
}

/**
 * 截图路径数组类型（新版本使用FilesJSONField）
 * 后端返回的是字符串数组，每个元素是文件路径
 */
export type Screenshots = string[]

// ==================== 数据模型 ====================

/**
 * 账号列表项数据
 * 用于列表页面展示的简化版账号信息
 */
export interface AccountListItem {
    /** 账号ID */
    id: number
    /** 提交审核后生成的对外账号编号 */
    account_number?: number | null
    /** 账号状态 */
    status: AccountStatusEnum
    /** 账号标题 */
    title?: string
    game_version?: 'delta' | 'delta_steam'
    billing_mode?: 'fixed' | 'proportional'
    support_platform?: 'all' | 'pc' | 'mobile'
    min_rental_days?: number
    max_rental_days?: number
    consumption_plan_key?: 'small' | 'medium' | 'large' | string
    consumption_plan_name?: string
    daily_consumption_m?: number | string
    rental_discount_percent?: number
    max_rentable_days?: number
    verification_screenshots?: Record<string, string[]>
    /** 创建时间 */
    created_at: string
    /** 发布时间 */
    published_at?: string | null
    /** 主图 */
    main_image: string
    /** 哈夫币数量(M) */
    haf_coins: number
    /** 体力等级 */
    stamina: number
    /** 负重等级 */
    carrying_capacity: number
    /** 保险格数 */
    insurance_slots: InsuranceSlotsEnum
    /** KD值 */
    kd?: string | null
    /** 六级头盔数量 */
    level6_helmets: number
    level6_helmet_unit_price?: number
    /** 六级护甲数量 */
    level6_armor: number
    level6_armor_unit_price?: number
    /** AW红蛋数量 */
    aw_red_eggs: number
    aw_red_egg_unit_price?: number
    /** 其他红蛋数量 */
    other_red_eggs: number
    untouched_assets?: string
    weapon_skin_count?: number
    collectible_skin_count?: number
    vehicle_skin_count?: number
    charm_count?: number
    /** 是否拥有刀皮 */
    has_knife_skins: boolean
    /** 刀皮列表 */
    knife_skins: GameSkin[]
    /** 是否拥有枪皮 */
    has_gun_skins: boolean
    /** 枪皮列表 */
    gun_skins: GunSkin[]
    /** 是否拥有干员皮 */
    has_operator_skins: boolean
    /** 干员皮列表 */
    operator_skins: OperatorSkin[]
    /** 租金(分) */
    rental_price: number
    /** 佣金(分) */
    commission: number
    /** 押金金额(分) */
    deposit_amount: number
    /** 管理员折扣百分比（1-99，如80表示8折） */
    admin_discount_percent?: number | null
    /** 哈夫币价比：租金元/哈夫币M，越低越划算 */
    haf_price_ratio?: number | null
    /** 哈夫币价比分档 */
    haf_ratio_tier?: 'low' | 'middle' | 'high' | null
    /** 是否特惠账号 */
    is_special_offer?: boolean
    /** 原始租金(分) - 折扣前 */
    original_rental_price?: number
    /** 押金减免金额(分) */
    admin_deposit_reduction?: number
    /** 原始押金(分) - 减免前 */
    original_deposit?: number
    /** 原始佣金(分) */
    original_commission?: number
    /** 是否有管理员调整 */
    has_admin_adjustment?: boolean
    /** 租赁次数 */
    rental_count: number
    /** 浏览次数 */
    view_count: number
    /** 登录平台（用于卡片展示图标） */
    login_method?: LoginMethodEnum
    /** 账号实名是否为本人 */
    is_real_name_owner: boolean
    /** 常用登录省份(可选) */
    common_login_province?: string
    /** 账号所有者 */
    owner: User
    /** 游戏段位 */
    rank: GameRank
    /** 锁定到期时间 */
    locked_until?: string | null
    /** 提交审核时间 */
    submitted_for_audit_at?: string | null
    /** 群二维码链接(可选) */
    group_qr_code?: string
    /** 群二维码图片(可选) */
    group_qr_image?: string
    /** 账号备注(可选) */
    notes?: string
}

/**
 * 账号详情数据
 * 包含账号的所有详细信息，用于详情页展示
 */
export interface AccountDetail {
    /** 账号ID */
    id: number
    /** 提交审核后生成的对外账号编号 */
    account_number?: number | null
    /** 账号状态 */
    status: AccountStatusEnum
    /** 账号标题 */
    title?: string
    game_version?: 'delta' | 'delta_steam'
    billing_mode?: 'fixed' | 'proportional'
    support_platform?: 'all' | 'pc' | 'mobile'
    min_rental_days?: number
    max_rental_days?: number
    consumption_plan_key?: 'small' | 'medium' | 'large' | string
    consumption_plan_name?: string
    daily_consumption_m?: number | string
    rental_discount_percent?: number
    max_rentable_days?: number
    verification_screenshots?: Record<string, string[]>
    /** 创建时间 */
    created_at: string
    /** 更新时间 */
    updated_at: string
    /** 发布时间 */
    published_at?: string | null
    /** 哈夫币数量(M) */
    haf_coins: number
    /** 体力等级 */
    stamina: number
    /** 负重等级 */
    carrying_capacity: number
    /** 保险格数 */
    insurance_slots: InsuranceSlotsEnum
    /** 六级头盔数量 */
    level6_helmets: number
    level6_helmet_unit_price?: number
    /** 六级护甲数量 */
    level6_armor: number
    level6_armor_unit_price?: number
    /** AW红蛋数量 */
    aw_red_eggs: number
    aw_red_egg_unit_price?: number
    /** 其他红蛋数量 */
    other_red_eggs: number
    untouched_assets?: string
    weapon_skin_count?: number
    collectible_skin_count?: number
    vehicle_skin_count?: number
    charm_count?: number
    /** KD值 */
    kd?: string | null
    /** 是否拥有刀皮 */
    has_knife_skins: boolean
    /** 刀皮列表 */
    knife_skins: GameSkin[]
    /** 是否拥有枪皮 */
    has_gun_skins: boolean
    /** 枪皮列表 */
    gun_skins: GunSkin[]
    /** 是否拥有干员皮 */
    has_operator_skins: boolean
    /** 干员皮列表 */
    operator_skins: OperatorSkin[]
    /** 租金(分) */
    rental_price: number
    /** 佣金(分) */
    commission: number
    /** 押金金额(分) */
    deposit_amount: number
    /** 管理员折扣百分比（1-99，如80表示8折） */
    admin_discount_percent?: number | null
    /** 哈夫币价比：租金元/哈夫币M，越低越划算 */
    haf_price_ratio?: number | null
    /** 哈夫币价比分档 */
    haf_ratio_tier?: 'low' | 'middle' | 'high' | null
    /** 是否特惠账号 */
    is_special_offer?: boolean
    /** 原始租金(分) - 折扣前 */
    original_rental_price?: number
    /** 押金减免金额(分) */
    admin_deposit_reduction?: number
    /** 原始押金(分) - 减免前 */
    original_deposit?: number
    /** 原始佣金(分) */
    original_commission?: number
    /** 是否有管理员调整 */
    has_admin_adjustment?: boolean
    /** 租赁次数 */
    rental_count: number
    /** 浏览次数 */
    view_count: number
    /** 登录平台 */
    login_method: LoginMethodEnum
    /** 登录平台显示名称 */
    login_method_display: string
    /** 登录方式 */
    login_sub_method?: LoginSubMethodEnum | null
    /** 登录方式显示名称 */
    login_sub_method_display: string
    /** QQ账号 */
    qq_account: string
    /** QQ密码 */
    qq_password: string
    /** Steam账号（仅拥有者可能看到字段值） */
    steam_account?: string
    /** 账号实名是否为本人 */
    is_real_name_owner: boolean
    /** 常用登录省份(可选) */
    common_login_province?: string
    /** 账号所有者 */
    owner: UserDetail
    /** 游戏段位 */
    rank: GameRank
    /** 账号截图（文件路径数组） */
    screenshots: Screenshots
    /** 群二维码链接(可选) */
    group_qr_code?: string
    /** 群二维码图片(可选) */
    group_qr_image?: string
    /** 账号备注(可选) */
    notes?: string
    /** 锁定到期时间 */
    locked_until?: string | null
    /** 提交审核时间 */
    submitted_for_audit_at: string
    /** 审核历史 */
    audit_history: string
}

// ==================== 请求类型 ====================

/**
 * 账号列表请求参数
 * 用于账号列表页面的查询
 */
export interface AccountListRequest {
    /** 页码，从1开始 */
    page?: number
    /** 每页数量 */
    size?: number
    /** 账号状态筛选 */
    status?: AccountStatusEnum
}

/**
 * 账号搜索请求参数
 * 继承列表请求参数，增加搜索条件
 */
export interface AccountSearchRequest extends AccountListRequest {
    game_version?: 'delta' | 'delta_steam'
    billing_mode?: 'fixed' | 'proportional'
    support_platform?: 'all' | 'pc' | 'mobile'
    min_days?: number
    max_days?: number
    /** AW>50 (AW红蛋数量 > 50) */
    is_aw_rich?: boolean
    /** 保险9格 */
    is_insurance_9?: boolean
    /** 热门账号 (租赁次数 > 5) */
    is_popular?: boolean
    /** 最新发布 (24小时内发布) */
    is_recent?: boolean
    /** 特勤处满级 (体力7级且负重7级) */
    is_special_maxed?: boolean
    /** 关键词搜索（账号ID、用户昵称、段位名称、标题） */
    keyword?: string
    /** 最多哈夫币 */
    max_haf_coins?: number
    /** 最高租金(元) */
    max_price?: number
    /** 最少哈夫币 */
    min_haf_coins?: number
    /** 最低租金(元) */
    min_price?: number
    /** 刀皮ID（逗号分隔字符串，多选且为 AND 语义，兼容旧参数 skins） */
    skins?: string
    /** 枪皮ID（逗号分隔字符串，多选且为 AND 语义） */
    gun_skins?: string
    /** 干员皮ID（逗号分隔字符串，多选且为 AND 语义） */
    operator_skins?: string
    /** 段位ID（逗号分隔字符串，多选 OR） */
    ranks?: string
    /** 保险格数（逗号分隔字符串，多选 OR） */
    insurance_slots?: string
    /** 特勤处筛选（逗号分隔），格式：s7=7体，l7=7负；任一匹配即可 */
    special_forces?: string
    /** 登录平台（多选逗号） */
    login_method?: string
    /** 常用登录省份 */
    common_login_province?: string
    /** 哈夫币价比分档：low/middle/high */
    haf_ratio_tier?: 'low' | 'middle' | 'high'
    /** 特惠区 */
    is_special_offer?: boolean
    /** 六级头盔数量范围（逗号分隔字符串），如 1,200 */
    head_range?: string
    /** 六级护甲数量范围（逗号分隔字符串），如 1,200 */
    armor_range?: string
    /** KD范围（逗号分隔字符串），如 0,100 */
    kd_range?: string
    aw_red_eggs_range?: string
    weapon_skin_count_range?: string
    collectible_skin_count_range?: string
    vehicle_skin_count_range?: string
    charm_count_range?: string
    untouched_assets?: string
    /** 排序项，支持 -favorite_count 实现热门排序 */
    ordering?: 'created_at' | '-created_at' | 'rental_price' | '-rental_price' | 'haf_coins' | '-haf_coins' | 'favorite_count' | '-favorite_count' | 'haf_ratio' | '-haf_ratio'
}

export interface AccountAutoFetchPreviewRequest {
    auth_token?: string
    qq?: string
}

export interface AccountAutoFetchPreviewResponse {
    supported: boolean
    manual_fallback: boolean
    message: string
    source?: string | null
    confidence: number
    candidate?: Partial<AccountCreateRequest> | null
    field_report?: AccountAutoFetchFieldReport
    raw_payload?: Record<string, unknown>
}

export interface AccountAutoFetchFieldReport {
    filled?: string[]
    missing?: string[]
    readable?: string[]
    issues?: string[]
}

export interface AccountAutoFetchSessionStartResponse {
    supported: boolean
    manual_fallback: boolean
    message: string
    session_id?: string
    status?: 'waiting' | 'confirmed' | 'expired'
    qr_code?: string | null
    qr_url?: string | null
    expires_in?: number
}

export interface AccountAutoFetchSessionStatusResponse {
    status: 'waiting' | 'confirmed' | 'expired'
    supported?: boolean
    manual_fallback: boolean
    message: string
    source?: string | null
    confidence?: number
    candidate?: Partial<AccountCreateRequest> | null
    field_report?: AccountAutoFetchFieldReport
    raw_payload?: Record<string, unknown>
}

export interface AccountAutoFetchAuthStartResponse {
    supported: boolean
    manual_fallback: boolean
    message: string
    session_id?: string
    status?: 'redirect' | 'waiting' | 'confirmed' | 'expired'
    auth_url?: string | null
    expires_in?: number
}

export interface AccountAutoFetchAuthStatusResponse {
    status: 'waiting' | 'confirmed' | 'expired'
    supported?: boolean
    manual_fallback: boolean
    message: string
    source?: string | null
    confidence?: number
    candidate?: Partial<AccountCreateRequest> | null
    field_report?: AccountAutoFetchFieldReport
    raw_payload?: Record<string, unknown>
}

/**
 * 账号创建请求参数
 * 用于创建新账号
 */
export interface AccountCreateRequest {
    /** 账号标题 */
    title?: string
    game_version?: 'delta' | 'delta_steam'
    billing_mode?: 'fixed' | 'proportional'
    support_platform?: 'all' | 'pc' | 'mobile'
    min_rental_days?: number
    max_rental_days?: number
    consumption_plan_key?: 'small' | 'medium' | 'large' | string
    daily_consumption_m?: number | string
    rental_discount_percent?: number
    verification_screenshots?: Record<string, string[]>
    /** 账号截图（文件路径数组） */
    screenshots?: Screenshots
    /** 刀皮ID列表 */
    knife_skins?: number[]
    /** 枪皮ID列表 */
    gun_skins?: number[]
    /** 干员皮ID列表 */
    operator_skins?: number[]
    /** QQ密码（仅写入） */
    qq_password?: string
    /** 哈夫币数量(M) */
    haf_coins?: number
    /** KD值 */
    kd?: string | null
    /** 体力等级 (0-7级) */
    stamina?: number
    /** 负重等级 (0-7级) */
    carrying_capacity?: number
    /** 保险格数 */
    insurance_slots?: InsuranceSlotsEnum
    /** 六级头盔数量 */
    level6_helmets?: number
    level6_helmet_unit_price?: number
    /** 六级护甲数量 */
    level6_armor?: number
    level6_armor_unit_price?: number
    /** AW红蛋数量 */
    aw_red_eggs?: number
    aw_red_egg_unit_price?: number
    /** 其他红蛋数量 */
    other_red_eggs?: number
    /** 是否拥有刀皮 */
    has_knife_skins?: boolean
    untouched_assets?: string
    weapon_skin_count?: number
    collectible_skin_count?: number
    vehicle_skin_count?: number
    charm_count?: number
    /** 是否拥有枪皮 */
    has_gun_skins?: boolean
    /** 是否拥有干员皮 */
    has_operator_skins?: boolean
    /** 押金金额(分) */
    deposit_amount?: number
    /** 登录平台 */
    login_method?: LoginMethodEnum
    /** 登录方式 */
    login_sub_method?: LoginSubMethodEnum | null
    /** QQ账号 */
    qq_account?: string
    /** Steam账号 */
    steam_account?: string
    /** 账号实名是否为本人 */
    is_real_name_owner?: boolean
    /** 常用登录省份(可选) */
    common_login_province?: string
    /** 群二维码链接(可选) */
    group_qr_code?: string
    /** 群二维码图片(可选) */
    group_qr_image?: string
    /** 账号备注(可选) */
    notes?: string
    /** 游戏段位 */
    rank?: number | null
}

/**
 * 账号更新请求参数
 * 用于更新现有账号，包含ID字段
 */
export interface AccountUpdateRequest extends AccountCreateRequest {
    /** 账号ID（更新时必需） */
    id: number
    /** 租金（只读，系统自动计算） */
    rental_price?: number
    /** 佣金（只读，系统自动计算） */
    commission?: number
    /** 创建时间（只读） */
    created_at?: string
    /** 提交审核时间 */
    submitted_for_audit_at?: string | null
}

// ==================== 响应类型 ====================

/**
 * 账号列表响应
 * 使用项目标准分页格式，经过响应拦截器处理后直接返回 PaginationData
 */
export type AccountListResponse = PaginationData<AccountListItem>

/**
 * 账号搜索响应
 * 与列表响应使用相同格式，经过响应拦截器处理后直接返回 PaginationData
 */
export type AccountSearchResponse = PaginationData<AccountListItem>

/**
 * 账号详情响应
 * 经过响应拦截器处理后直接返回业务数据
 */
export type AccountDetailResponse = AccountDetail

/**
 * 账号创建响应
 * 经过响应拦截器处理后直接返回创建的账号详情
 */
export type AccountCreateResponse = AccountDetail

/**
 * 账号更新响应
 * 经过响应拦截器处理后直接返回更新后的账号详情
 */
export type AccountUpdateResponse = AccountDetail

/**
 * 账号删除响应
 * 删除操作通常返回空或成功消息，经过响应拦截器处理
 */
export type AccountDeleteResponse = void

/**
 * 提交审核响应
 * 操作成功，经过响应拦截器处理后可能返回空或操作结果
 */
export type AccountSubmitAuditResponse = void

/**
 * 撤销审核响应
 * 操作成功，经过响应拦截器处理后可能返回空或操作结果
 */
export type AccountCancelAuditResponse = void

/**
 * 下架账号响应
 * 操作成功，经过响应拦截器处理后可能返回空或操作结果
 */
export type AccountUnpublishResponse = void

// ==================== 统计相关类型 ====================

/**
 * 账号统计数据
 * 用于展示用户账号的各项统计信息
 */
export interface AccountStats {
    /** 总账号数 */
    total_accounts: number
    /** 已发布账号数 */
    published_accounts: number
    /** 草稿账号数 */
    draft_accounts: number
    /** 租赁中账号数 */
    rented_accounts: number
    /** 审核中账号数 */
    pending_accounts: number
    /** 已拒绝账号数 */
    rejected_accounts: number
    /** 待号主确认账号数 */
    pending_confirm_accounts: number
    /** 锁定中账号数 */
    locked_accounts: number
    /** 待最终确认账号数 */
    await_final_accounts: number
}

/**
 * 账号统计响应
 * 经过响应拦截器处理后直接返回统计数据
 */
export type AccountStatsResponse = AccountStats

// ==================== 账号收藏相关类型 ====================

/**
 * 账号收藏详情
 */
export interface AccountFavoriteDetail {
    id: number | null
    title: string
    status: AccountStatusEnum
    status_display: string
    haf_coins: number
    rental_price: number
    deposit_amount: number
    insurance_slots: InsuranceSlotsEnum
    screenshots: Screenshots
    view_count: number
    rental_count: number
    rank: string | null
}

/**
 * 账号收藏列表项
 */
export interface AccountFavorite {
    /** 收藏记录ID */
    id: number
    /** 收藏创建时间 */
    created_at: string
    /** 账号状态标签 */
    account_status_label: AccountFavoriteStatusLabel
    /** 账号详情 */
    account_detail: AccountFavoriteDetail
} 
